/**
 * Shared Deal Desk Discount Converter
 * Used by both client and server to convert deal desk discounts to standard format
 */

/**
 * Generates a UUID v4 for frontend-created deal desk discounts
 * Backend will replace this with a proper UUID when saved
 */
export function generateUUID() {
   return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
   })
}

/**
 * Checks if a string is a valid UUID format
 */
export function isUUID(str) {
   const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
   return uuidRegex.test(str)
}

/**
 * Builds a human-readable description for a deal desk discount
 * @param {string[]} applyToArray - Array of product codes
 * @param {number|null} startMonth - Start month (optional)
 * @param {number|null} endMonth - End month (optional)
 * @param {Object} product_list - Product list to resolve codes to names
 * @returns {string} Human-readable description
 */
function buildDiscountDescription(
   applyToArray,
   startMonth,
   endMonth,
   product_list = {},
) {
   // Build product names list
   const productNames = applyToArray.map((code) => {
      const product = product_list[code]
      return product?.ProductName || code
   })

   // Build month range string
   let monthRange = ''
   if (startMonth && endMonth) {
      monthRange =
         startMonth === endMonth
            ? `Month ${startMonth}`
            : `Months ${startMonth}-${endMonth}`
   } else if (startMonth) {
      monthRange = `Starting Month ${startMonth}`
   } else if (endMonth) {
      monthRange = `Through Month ${endMonth}`
   }

   // Build description
   const productsPart = `Applies to: ${productNames.join(', ')}`
   return monthRange ? `${productsPart} | ${monthRange}` : productsPart
}

/**
 * Converts a Deal Desk Discount (snake_case) to standard Discount format (PascalCase)
 * @param {Object} dealDeskDiscount - The deal desk discount to convert (snake_case keys)
 * @param {Object} product_list - Optional product list to resolve product codes to names for description
 * @returns {Object} A standard Discount object that can be inserted into discount_list
 */
export function convertDealDeskDiscountToStandardFormat(
   dealDeskDiscount,
   product_list = {},
) {
   // Use existing UUID if present (from backend), otherwise generate one
   const discountCode =
      dealDeskDiscount.id && dealDeskDiscount.id !== ''
         ? dealDeskDiscount.id
         : generateUUID()

   // Handle apply_to - should always be an array
   const applyToArray = Array.isArray(dealDeskDiscount.apply_to)
      ? dealDeskDiscount.apply_to
      : []

   const applyTo = applyToArray.join(',')

   // Build human-readable description with product names and month range
   const discountDescription = buildDiscountDescription(
      applyToArray,
      dealDeskDiscount.start_month,
      dealDeskDiscount.end_month,
      product_list,
   )

   // Generate default name based on billing frequency
   const discountName =
      dealDeskDiscount.billing_freq === 'monthly'
         ? 'Additional Monthly Discount'
         : 'Additional One Time Discount'

   // Determine disclaimer and ShowDisclaimerOn
   // If disclaimer is non-empty, show it on all products this discount applies to
   const disclaimer = dealDeskDiscount.disclaimer || ''
   const showDisclaimerOn = disclaimer.trim() !== '' ? applyTo : ''

   // Display order: negative to ensure deal desk discounts appear first
   const displayOrder = -10 + (dealDeskDiscount.priority || 0)

   return {
      // Use UUID as discount code
      DiscountCode: discountCode,
      DiscountName: discountName,
      DiscountDescription: discountDescription,
      DiscountType: dealDeskDiscount.discount_type,
      // Convert to number for consistency with pricebook discounts (DB returns DECIMAL as string)
      DiscountAmount: parseFloat(dealDeskDiscount.discount_amount) || 0,
      ApplyToPDFDisplayedValue: dealDeskDiscount.apply_to_pdf_displayed_value ?? true,

      // Convert array to comma-separated string
      ApplyTo: applyTo,

      // Add billing_freq as BillingFreq
      BillingFreq: dealDeskDiscount.billing_freq,

      // Commission fields
      AppliesToCommission: dealDeskDiscount.applies_to_commission || false,
      // Convert to number for consistency (DB returns DECIMAL as string)
      CommissionDiscountAmount:
         dealDeskDiscount.commission_discount_amount != null
            ? parseFloat(dealDeskDiscount.commission_discount_amount)
            : null,

      // Month range for prepayment discounts
      StartMonth: dealDeskDiscount.start_month ?? null,
      EndMonth: dealDeskDiscount.end_month ?? null,

      // Deal desk discounts are always applied and cannot be unchecked
      AlwaysApply: true,
      DefaultOn: false,

      // Always true for deal desk discounts
      DealDeskDiscount: true,

      // Input type based on discount type
      InputType:
         dealDeskDiscount.discount_type === 'percent' ? 'percent' : 'number',

      // Display at top (negative order to ensure deal desk discounts appear first)
      DisplayOrder: displayOrder,

      // Priority from deal desk discount
      Priority: dealDeskDiscount.priority || 0,

      // Standard discount fields with defaults
      BookId: 0,
      DiscId: 0,
      AccountFlags: '',
      DependsOn: '',
      Disclaimer: disclaimer,
      EmpHigh: 999999,
      EmpLow: 0,
      EndDt: '',
      HeroDiscount: false,
      LookupAmountField: '',
      LookupProduct: '',
      LookupProductField: '',
      MinRequiredPrepayme: '',
      OpportunityTypes: '',
      ProductFamily: '',
      PromoUTM: '',
      Promotional: true,
      RequiredProducts: '',
      RoleRequired: '',
      ShowDisclaimerOn: showDisclaimerOn,
      StartDt: '',
      SFDealDeskType: '',
      MinRequiredPrepayment: '',
      MaxDiscount: 0,
   }
}
